import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  OutlinedInput,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PencilIcon from "assets/svg/PencilIcon";
import UserProfileIcon from "assets/svg/UserProfileIcon";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import { profileUpdateSchema } from "validations/ValidationsSchema";
import theme, { responsiveFontSize } from "themes/Theme";
import MailIcon from "assets/svg/MailIcon";
import ChangeEmailAddress from "./ChangeEmailAddress";
import * as motion from "motion/react-client";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reduxStore/store";
import { FETCH_PROFILE_TYPE } from "reduxStore/actions/authActionType";
import { DateTime } from "luxon";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { updateProfile } from "services/apiService";
import CloseIcon from "assets/svg/CloseIcon";
import EditIcon from "@mui/icons-material/Edit";

const ProfileManagement = () => {
  const [editable, setEditable] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // const [image, setImage] = useState<string | null>(null);
  // const [crop, setCrop] = useState({ x: 0, y: 0 });
  // const [zoom, setZoom] = useState(1);
  // const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: FETCH_PROFILE_TYPE });
  }, []);

  const toggleEditable = () => {
    setEditable(!editable);
  };

  const validate = (values: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    dob: string;
    profilePic: string;
  }) => {
    const result = profileUpdateSchema.safeParse(values);
    if (!result.success) {
      return result.error.format();
    }
    return {};
  };

  // const onDrop = useCallback(async (acceptedFiles: File[]) => {
  //   if (acceptedFiles.length === 0) return;

  //   const file = acceptedFiles[0];

  //   // Compress Image
  //   const compressedFile = await imageCompression(file, {
  //     maxSizeMB: 0.5,
  //     maxWidthOrHeight: 1024,
  //   });
  //   const reader = new FileReader();

  //   reader.onload = () => setImage(reader.result as string);
  //   reader.readAsDataURL(compressedFile);
  // }, []);

  // const { getRootProps, getInputProps } = useDropzone({
  //   onDrop,
  //   accept: { "image/*": [] },
  //   multiple: false,
  // });

  // // Crop Image
  // const onCropComplete = async (_: any, croppedAreaPixels: any) => {
  //   if (!image) return;

  //   try {
  //     const croppedBlob = await getCroppedImg(image, croppedAreaPixels);

  //     const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
  //       type: "image/jpeg",
  //     });
  //     const compressedCroppedBlob = await imageCompression(croppedFile, {
  //       maxSizeMB: 0.3,
  //     });
  //     const base64String = await convertBlobToBase64(compressedCroppedBlob);
  //     setCroppedImage(base64String);
  //     buttonRef?.current?.focus();
  //   } catch (error) {
  //     console.error("Cropping or compression failed:", error);
  //   }
  // };

  const handleUpdateProfile = async (
    values: {
      firstName: string;
      lastName: string;
      phoneNumber: string;
      dob: string;
      profilePic: string;
    },
    resetForm: () => void
  ) => {
    try {
      const body = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        dob: values.dob,
        // profilePic: croppedImage || values.profilePic,
        profilePic: values.profilePic, // Keep this line as a placeholder or remove if not needed
      };
      const response = await updateProfile(body);
      if (response && response.status === 200) {
        toast.success("Profile updated successfully");
        setEditable(false);
        dispatch({ type: FETCH_PROFILE_TYPE });
        resetForm();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    }
  };

  const EditableFields = () => {
    return (
      <Formik
        initialValues={{
          firstName: user?.user?.data?.personal_details?.firstName || "",
          lastName: user?.user?.data?.personal_details?.lastName || "",
          phoneNumber: user?.user?.data?.personal_details?.phoneNumber || "",
          dob: user?.user?.data?.personal_details?.dob || "",
          profilePic: user?.user?.data?.personal_details?.profilePic || "",
        }}
        validate={validate}
        onSubmit={(values, { resetForm }) => {
          handleUpdateProfile(values, resetForm);
        }}
      >
        {({ values, handleSubmit, errors, touched, setFieldValue }) => (
          <Form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1em",
              marginBottom: "1em",
            }}
          >
            <Grid2 container spacing={2} wrap="wrap">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  First Name
                </Typography>
                <Field name="firstName">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="text"
                      size="small"
                      margin="dense"
                      slotProps={commonStyle.inputSlotTextStyle}
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Last Name
                </Typography>
                <Field name="lastName">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="text"
                      margin="dense"
                      size="small"
                      fullWidth
                      slotProps={commonStyle.inputSlotTextStyle}
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Phone Number
                </Typography>
                <Field name="phoneNumber">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="text"
                      margin="dense"
                      size="small"
                      fullWidth
                      slotProps={commonStyle.inputSlotTextStyle}
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Date Of Birth
                </Typography>
                <Field name="dob">
                  {({ field, form, meta }: any) => (
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                      <DatePicker
                        value={
                          field.value ? DateTime.fromISO(field.value) : null
                        }
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            field.name,
                            newValue ? newValue.toISODate() : ""
                          );
                        }}
                        format="yyyy-MM-dd"
                        disableFuture
                        maxDate={DateTime.now()}
                        sx={{
                          width: "100%",
                        }}
                        slotProps={{
                          textField: {
                            margin: "dense",
                            variant: "outlined",
                            size: "small",
                            slotProps: {
                              input: {
                                sx: {
                                  fontSize: "0.875rem",
                                  color: theme.palette.text.primary,
                                  fontWeight: 400,
                                },
                              },
                            },
                            error: meta.touched && Boolean(meta.error),
                            helperText:
                              meta.touched && meta.error
                                ? meta.error._errors?.[0]
                                : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Field>
              </Grid2>
            </Grid2>
            <PrimaryButton
              ref={buttonRef}
              title="Update Profile"
              onClick={() => {
                console.log("Handle submit clicked");
                handleSubmit();
              }}
              viewStyle={{
                borderRadius: "999px",
                padding: "10px 24px",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                color: "#fff",
                fontWeight: 600,
                fontSize: responsiveFontSize(14),
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.2)",
                  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                },
              }}
            />
          </Form>
        )}
      </Formik>
    );
  };

  const NonEditableFields = () => {
    return (
      <Grid2 container spacing={2} wrap="wrap" mt={4}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Typography sx={commonStyle.inputLabelStyle}>First Name</Typography>
          <Typography sx={commonStyle.fieldValueStyle}>
            {user?.user?.data?.personal_details?.firstName}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Typography sx={commonStyle.inputLabelStyle}>Last Name</Typography>
          <Typography sx={commonStyle.fieldValueStyle}>
            {user?.user?.data?.personal_details?.lastName}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Typography sx={commonStyle.inputLabelStyle}>Phone Number</Typography>
          <Typography sx={commonStyle.fieldValueStyle}>
            {user?.user?.data?.personal_details?.phoneNumber
              ? user?.user?.data?.personal_details?.phoneNumber
              : "-"}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Typography sx={commonStyle.inputLabelStyle}>
            Date Of Birth
          </Typography>
          <Typography sx={commonStyle.fieldValueStyle}>
            {user?.user?.data?.personal_details?.dob
              ? user?.user?.data?.personal_details?.dob
              : "-"}
          </Typography>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <Typography sx={commonStyle.inputLabelStyle}>Status</Typography>
          <Typography
            sx={{
              ...commonStyle.fieldValueStyle,
              color:
                user?.user?.data?.personal_details?.status === "Active"
                  ? theme.palette.custom?.green
                  : theme.palette.custom?.orange,
            }}
          >
            {user?.user?.data?.personal_details?.status}
          </Typography>
        </Grid2>
      </Grid2>
    );
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      transition={{
        type: "spring",
        stiffness: 30,
      }}
    >
      <Box sx={{ ...styles.container, p: { xs: 1, sm: 3 } }}>
        <Box
          sx={{
            ...styles.flexSpaceBetween,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={{ xs: 1, sm: 1 }}
          >
            <Avatar sx={{ ...styles.avatarStyle, bgcolor: "#fffede" }}>
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: responsiveFontSize(24),
                  textTransform: "uppercase",
                }}
              >
                {user?.user?.data?.personal_details?.firstName?.charAt(0)}
              </Typography>
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: responsiveFontSize(24),
                  textTransform: "uppercase",
                }}
              >
                {user?.user?.data?.personal_details?.lastName?.charAt(0)}
              </Typography>
            </Avatar>
            <Box>
              <Typography
                sx={{
                  ...styles.profileName,
                  fontSize: {
                    xs: responsiveFontSize(16),
                    sm: responsiveFontSize(20),
                  },
                }}
              >
                {user?.user?.data?.personal_details?.firstName}{" "}
                {user?.user?.data?.personal_details?.lastName}
              </Typography>
              <Typography
                sx={{
                  ...styles.profileEmail,
                  fontSize: {
                    xs: responsiveFontSize(13),
                    sm: responsiveFontSize(16),
                  },
                }}
              >
                {user?.user?.data?.personal_details?.email}
              </Typography>
            </Box>
          </Stack>
          <PrimaryButton
            title={editable ? "Cancel" : "Edit"}
            onClick={toggleEditable}
            rightIcon={editable ? <CloseIcon color="#fff" /> : <PencilIcon />}
            viewStyle={{
              borderRadius: "999px",
              padding: "10px 24px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: "#fff",
              fontWeight: 600,
              fontSize: responsiveFontSize(14),
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.2)",
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          />
        </Box>
        {editable ? <EditableFields /> : <NonEditableFields />}
        <Stack direction="row" alignItems="center" gap={1} mt={2}>
          <Typography
            sx={{
              ...styles.myEmail,
              fontSize: {
                xs: responsiveFontSize(13),
                sm: responsiveFontSize(16),
              },
            }}
          >
            My Email Address
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: "row", sm: "row" }}
          alignItems={{ xs: "center", sm: "center" }}
          gap={2}
          mt={1}
          // justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                ...styles.mailIconContainer,
                width: { xs: 36, sm: 50 },
                height: { xs: 36, sm: 50 },
              }}
            >
              <MailIcon height={28} width={28} />
            </Box>
            <Typography
              sx={{
                ...styles.editEmailAddressText,
                fontSize: {
                  xs: responsiveFontSize(12),
                  sm: responsiveFontSize(14),
                },
              }}
            >
              {user?.user?.data?.personal_details?.email}
            </Typography>
          </Box>
          <IconButton
            sx={{
              p: 0,
              m: 0,
              background: "none",
              "&:hover": { background: "none" },
            }}
            onClick={() => setEditEmail(true)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
        <ChangeEmailAddress
          open={editEmail}
          existingEmail={user?.user?.data?.personal_details?.email || ""}
          handleClose={() => setEditEmail(false)}
          onEmailUpdateSuccess={() => dispatch({ type: FETCH_PROFILE_TYPE })}
        />
      </Box>
    </motion.div>
  );
};
export default ProfileManagement;
const styles = {
  container: {
    backgroundColor: "#FFFFFF",
    boxShadow:
      "rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
    borderRadius: "1em",
    marginTop: { xs: "1em", sm: "2em" },
    // p is now handled inline for responsiveness
  },
  flexSpaceBetween: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  profileName: {
    fontSize: responsiveFontSize(20),
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  profileEmail: {
    fontSize: responsiveFontSize(16),
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  myEmail: {
    fontSize: responsiveFontSize(16),
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  emailEditContainer: {
    backgroundColor: theme.palette.primary.main,
    width: 30,
    height: 30,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  mailIconContainer: {
    backgroundColor: "rgba(65, 130, 249, 0.1)",
    width: "50px",
    height: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  editEmailAddressText: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  editEmailAddressDurationText: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  avatarStyle: {
    width: 70,
    height: 70,
    aspectRatio: "1/1",
    objectFit: "cover",
    border: "1px solid #ccc", // Added circle border
    borderRadius: "50%", // Ensure it's a circle
  },
};

/*
async function getCroppedImg(imageSrc: string, cropArea: any): Promise<Blob> {
  const img = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Canvas context not available");

  canvas.width = cropArea.width;
  canvas.height = cropArea.height;

  ctx.drawImage(
    img,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to create cropped image blob"));
    }, "image/jpeg");
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
  });
}

const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
};
*/
